import { useMemo, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import type { PodReport, Resources } from '../api/clusters';
import { formatBytes, formatCores, hasFailed, hasProblem } from './cluster-summary';

function UsageCell({
  usage,
  request,
  limit,
  format,
}: {
  usage: number | null;
  request: number;
  limit: number | null;
  format: (value: number) => string;
}) {
  const nearLimit = usage !== null && limit !== null && limit > 0 && usage / limit >= 0.9;
  return (
    <div className="whitespace-nowrap tabular-nums">
      <div className={nearLimit ? 'font-medium text-destructive' : undefined}>
        {usage === null ? '–' : format(usage)}
        {nearLimit && ' · near limit'}
      </div>
      <div className="text-xs text-muted-foreground">
        {request ? format(request) : 'no request'} / {limit === null ? 'no limit' : format(limit)}
      </div>
    </div>
  );
}

type SortKey = 'name' | 'cpu' | 'memory' | 'restarts';

function usageOf(pod: PodReport, key: keyof Resources): number {
  return pod.usage?.[key] ?? -1;
}

const SORTS: Record<SortKey, (a: PodReport, b: PodReport) => number> = {
  name: (a, b) =>
    Number(hasProblem(b)) - Number(hasProblem(a)) ||
    a.namespace.localeCompare(b.namespace) ||
    a.name.localeCompare(b.name),
  cpu: (a, b) => usageOf(b, 'cpuMillicores') - usageOf(a, 'cpuMillicores'),
  memory: (a, b) => usageOf(b, 'memoryBytes') - usageOf(a, 'memoryBytes'),
  restarts: (a, b) => b.restarts - a.restarts,
};

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortKey;
  onSort: (key: SortKey) => void;
}) {
  return (
    <TableHead aria-sort={sort === sortKey ? 'descending' : undefined}>
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {sort === sortKey && <ArrowDown className="h-3 w-3" aria-hidden />}
      </button>
    </TableHead>
  );
}

/** The pods of one group, problems first, sortable by usage and restarts. */
export function PodTable({ pods, showNode }: { pods: PodReport[]; showNode: boolean }) {
  const [sort, setSort] = useState<SortKey>('name');
  const shown = useMemo(() => [...pods].sort(SORTS[sort]), [pods, sort]);
  return (
    <div className="overflow-x-auto rounded-3xl border bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            <SortHeader label="Pod" sortKey="name" sort={sort} onSort={setSort} />
            {showNode && <TableHead>Node</TableHead>}
            <TableHead>Status</TableHead>
            <SortHeader label="Restarts" sortKey="restarts" sort={sort} onSort={setSort} />
            <SortHeader label="CPU" sortKey="cpu" sort={sort} onSort={setSort} />
            <SortHeader label="Memory" sortKey="memory" sort={sort} onSort={setSort} />
            <TableHead>Storage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map(pod => (
            <TableRow key={`${pod.namespace}/${pod.name}`}>
              <TableCell className="min-w-64">
                <div className="font-medium">{pod.name}</div>
                <div className="text-xs text-muted-foreground">
                  {pod.owner ? `${pod.owner.kind} ${pod.owner.name}` : 'No owner'}
                </div>
              </TableCell>
              {showNode && (
                <TableCell className="whitespace-nowrap text-sm">
                  {pod.node ?? 'Unscheduled'}
                </TableCell>
              )}
              <TableCell>
                <Badge variant={hasProblem(pod) || hasFailed(pod) ? 'destructive' : 'secondary'}>
                  {pod.reason ?? pod.phase}
                </Badge>
                <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                  {pod.readyContainers}/{pod.containers} ready
                </div>
              </TableCell>
              <TableCell className="tabular-nums">{pod.restarts}</TableCell>
              <TableCell>
                <UsageCell
                  usage={pod.usage?.cpuMillicores ?? null}
                  request={pod.requests.cpuMillicores}
                  limit={pod.limits.cpuMillicores}
                  format={formatCores}
                />
              </TableCell>
              <TableCell>
                <UsageCell
                  usage={pod.usage?.memoryBytes ?? null}
                  request={pod.requests.memoryBytes}
                  limit={pod.limits.memoryBytes}
                  format={formatBytes}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                {pod.ephemeralBytes !== null && <div>{formatBytes(pod.ephemeralBytes)} local</div>}
                {pod.volumes.map(volume => (
                  <div key={volume.name}>
                    {volume.claim}: {formatBytes(volume.usedBytes)} of{' '}
                    {formatBytes(volume.capacityBytes)}
                  </div>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
